// src/pages/TaskPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNextVideo, getAllEmotions, submitResponse } from '../services/api';
import VideoPlayer from '../components/VideoPlayer';
import EmotionWheel from '../components/EmotionWheel';
import LoadingSpinner from '../components/LoadingSpinner';
import { EMOTION_ORDER } from '../constants/emotions';


function TaskPage({ currentUserId, muted = false }) {
  const testMode = muted ? 'silent' : 'sound';
  const [currentVideo, setCurrentVideo] = useState(() => {
    const savedVideo = localStorage.getItem('emotionSurveyCurrentTaskVideo');
    try {
      return savedVideo ? JSON.parse(savedVideo) : null;
    } catch (e) {
      console.error("Error parsing saved video from localStorage", e);
      localStorage.removeItem('emotionSurveyCurrentTaskVideo');
      return null;
    }
  });

  const [taskNumber, setTaskNumber] = useState(() => {
    const savedTaskNumber = localStorage.getItem('emotionSurveyCurrentTaskNumber');
    return savedTaskNumber ? parseInt(savedTaskNumber, 10) : 1;
  });

  const [emotions, setEmotions] = useState([]);
  const [isVideoPlaybackFinished, setIsVideoPlaybackFinished] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(!currentVideo);
  const [hasLoadedFirstVideo, setHasLoadedFirstVideo] = useState(() => !!currentVideo);
  const [isLoadingEmotions, setIsLoadingEmotions] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVideoAlreadyPlayed, setIsVideoAlreadyPlayed] = useState(false);
  const [wheelLocked, setWheelLocked] = useState(false);
  
  const navigate = useNavigate();
  
  useEffect(() => {
    const soundDone = localStorage.getItem('emotionSurveySoundTestCompleted') === 'true';
    const silentDone = localStorage.getItem('emotionSurveySilentTestCompleted') === 'true';
    const currentDone = testMode === 'sound' ? soundDone : silentDone;

	if (currentDone) {
      navigate('/all-tests-complete', { replace: true });
    }
  }, [navigate, testMode]);
  
  useEffect(() => {
    localStorage.setItem('emotionSurveyCurrentTestMode', testMode);
  }, [testMode]);

  const fetchEmotions = useCallback(async () => {
    if (emotions.length > 0) {
        setIsLoadingEmotions(false);
        return;
    }
    setIsLoadingEmotions(true);
    try {
      const emotionsResponse = await getAllEmotions();
      let fetchedEmotions = emotionsResponse.data || [];
      
      fetchedEmotions.sort((a, b) => {
        const indexA = EMOTION_ORDER.indexOf(a.emotion_code);
        const indexB = EMOTION_ORDER.indexOf(b.emotion_code);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
      
      setEmotions(fetchedEmotions);
    } catch (err) {
      console.error("Error fetching emotions: ", err);
      setError(prevError => (prevError ? prevError + "\n" : "") + 'Не удалось загрузить список эмоций.');
    } finally {
      setIsLoadingEmotions(false);
    }
  }, [emotions.length]);

  const fetchNextVideoForTask = useCallback(async () => {
    if (!currentUserId) {
      navigate('/');
      return;
    }

    const shouldShowSpinner = !hasLoadedFirstVideo;

    if (shouldShowSpinner) {
      setIsLoadingVideo(true);
    }

    setIsVideoPlaybackFinished(false);
    setIsVideoAlreadyPlayed(false);
    setCurrentVideo(null);
    localStorage.removeItem('emotionSurveyCurrentTaskVideo');
    localStorage.removeItem('emotionSurveyCurrentTaskVideoPlayed');

    try {
      const videoResponse = await getNextVideo(currentUserId, testMode);
      if (videoResponse.data) {
        setCurrentVideo(videoResponse.data);
        localStorage.setItem('emotionSurveyCurrentTaskVideo', JSON.stringify(videoResponse.data));
		setHasLoadedFirstVideo(true);
      } else {
        setCurrentVideo(null);
        localStorage.removeItem('emotionSurveyCurrentTaskVideo');
        localStorage.removeItem('emotionSurveyCurrentTaskNumber');
        navigate(`/test-complete-${testMode}`);
      }
    } catch (err) {
      console.error("Error fetching next video: ", err);
      setError(prevError => (prevError ? prevError + "\n" : "") + 'Не удалось загрузить следующее видео.');
      setCurrentVideo(null);
      localStorage.removeItem('emotionSurveyCurrentTaskVideo');
    } finally {
      if (shouldShowSpinner) {
        setIsLoadingVideo(false);
      }
    }
  }, [currentUserId, navigate, testMode, hasLoadedFirstVideo]);

  useEffect(() => {
    fetchEmotions();
    if (currentUserId) {
      if (!currentVideo) {
        fetchNextVideoForTask();
      } else {
        setIsVideoPlaybackFinished(false);
        setIsLoadingVideo(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, fetchEmotions]);

  useEffect(() => {
    if (taskNumber > 0) {
        localStorage.setItem('emotionSurveyCurrentTaskNumber', taskNumber.toString());
    }
  }, [taskNumber]);
  
  useEffect(() => {
    if (currentVideo && currentVideo.video_id) {
      try {
        const saved = localStorage.getItem('emotionSurveyCurrentTaskVideoPlayed');
        const parsed = saved ? JSON.parse(saved) : {};
        if (parsed && parsed[currentVideo.video_id] === true) {
          setIsVideoAlreadyPlayed(true);
          setIsVideoPlaybackFinished(true);
        } else {
          setIsVideoAlreadyPlayed(false);
          setIsVideoPlaybackFinished(false);
        }
      } catch (e) {
        console.error('Error reading video start flag:', e);
        localStorage.removeItem('emotionSurveyCurrentTaskVideoPlayed');
        setIsVideoAlreadyPlayed(false);
        setIsVideoPlaybackFinished(false);
      }
    }
  }, [currentVideo]);


  const handleEmotionSelect = async (selectedEmotionId) => {
    if (
      isSubmitting ||
	  wheelLocked ||
      !currentVideo ||
      !currentUserId ||
      (!isVideoPlaybackFinished && !isVideoAlreadyPlayed)
    ) {
      if (!isVideoPlaybackFinished && !isVideoAlreadyPlayed && currentVideo) {
        setError("Пожалуйста, дождитесь окончания просмотра видео перед выбором эмоции.");
      }
      return;
    }
	
    setError('');
	setIsSubmitting(true);
    setWheelLocked(true);
    try {
      await submitResponse({
        userId: currentUserId,
        videoId: currentVideo.video_id,
        selectedEmotionId: selectedEmotionId,
		testMode: muted ? 'silent' : 'sound',
      });
      	  
      setTimeout(async () => {
        try {
          await fetchNextVideoForTask();
        } finally {
		  setTaskNumber(prev => prev + 1);
          setWheelLocked(false);

        }
      }, 1000);
    } catch (err) {
      console.error("Error submitting response: ", err);
      setError('Ошибка отправки ответа. Попробуйте еще раз.');
	  setWheelLocked(false);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleVideoPlaybackEnded = useCallback(() => {
    console.log("TaskPage: Video playback finished.");
    setIsVideoPlaybackFinished(true);
  }, []);
  
  const handleVideoStart = useCallback(() => {
    if (!currentVideo) return;
    try {
      const payload = { [currentVideo.video_id]: true };
      localStorage.setItem('emotionSurveyCurrentTaskVideoPlayed', JSON.stringify(payload));
    } catch (e) {
      console.error('Error saving video start flag:', e);
    }
  }, [currentVideo]);

  // --- ЛОГИКА РЕНДЕРИНГА ---
  
  if (isLoadingEmotions) {
    return (
      <div className="page-container">
        <LoadingSpinner />
        <p>Загрузка списка эмоций...</p>
      </div>
    );
  }

  if (isLoadingVideo && !hasLoadedFirstVideo) {
    return (
      <div className="page-container">
        <LoadingSpinner />
        <p>Загрузка видео...</p>
      </div>
    );
  }

  if (error && !currentVideo) {
    return (
      <div className="page-container error-message">
        <p>Произошла ошибка при загрузке задания:</p>
        <pre style={{ whiteSpace: 'pre-wrap' }}>{error}</pre>
        <button
          className="app-header-button"
          onClick={() => {
            setError('');
            fetchNextVideoForTask();
          }}
        >
          Попробовать снова
        </button>
      </div>
    );
  }
  
if (!currentVideo && !isLoadingVideo && !isLoadingEmotions && !error) {
    return (
      <div className="page-container">
        <p>Все задания выполнены. Перенаправление...</p>
      </div>
    );
  }

  // --- ОСНОВНАЯ РАЗМЕТКА JSX ---

  return (
    <div className="page-container task-page">
      <div className="task-header">
        <h2>Задание №{taskNumber}</h2>
        <p>Просмотрите видео и выберите эмоцию из списка. Видео запустится автоматически.</p>
      </div>
      
      <div className="video-player-section">
		{!isVideoPlaybackFinished && !isVideoAlreadyPlayed ? (
          currentVideo && currentVideo.video_filename ? (
            <VideoPlayer
              videoFilename={currentVideo.video_filename}
              videoKey={currentVideo.video_id}
              onVideoEnd={handleVideoPlaybackEnded}
              onVideoStart={handleVideoStart}
              muted={muted}
            />
          ) : (
            <div className="video-player-wrapper">
              <div className="fixation-point-container">
				<div className="fixation-point">●</div>
                <p>Подготовка видео...</p>
              </div>
            </div>
          )
        ) : (
          <EmotionWheel
            emotions={emotions}
            onSelectEmotion={handleEmotionSelect}
            disabled={
              wheelLocked ||
              isSubmitting ||
              !currentVideo ||
              (isLoadingVideo && !hasLoadedFirstVideo)
            }
          />
        )}
      </div>

      {error && <p className="error-message task-page-error">{error}</p>}
      

      {isSubmitting && <LoadingSpinner size="small" text="Отправка..." />}
      
      <div className="navigation-buttons task-page-nav-buttons">
        <button 
          onClick={() => navigate('/emotion-list')} 
          className="info-button app-header-button"
          disabled={isSubmitting}
        >
          Пояснение списка эмоций
        </button>
      </div>
    </div>
  );
}

export default TaskPage;